/**
 * Stars + confetti — ported from legacy js/stars.js + js/confetti.js.
 * Pure CSS transforms via dynamically-set custom properties, no canvas
 * loop, no library. Auto-cleanup on every spawn.
 */

const STAR_SVG =
  '<svg viewBox="0 0 20 20" aria-hidden="true">' +
    '<path d="M10 1.5l2.45 5.7L18.5 8l-4.5 4 1.4 6.5L10 15l-5.4 3.5L6 12 1.5 8l6.05-.8L10 1.5z"/>' +
  '</svg>';

const STAR_COLORS = [
  "#e0c388", "#e0c388", "#e0c388",     // gold (weighted)
  "#c9a96a",
  "#ffd24a",                            // bright yellow
  "#ff5fa2",                            // pink
  "#4ea3ff"                             // blue
];

const CONFETTI_COLORS = [
  "#e0c389", "#c8a96a", "#ff5544", "#3ddc88",
  "#4ea3ff", "#ff5fa2", "#a86bff", "#ffd24a"
];

const CONFETTI_SHAPES = ["square", "rect", "circle"] as const;

function prefersReducedMotion(): boolean {
  return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

let lastConfettiAt = 0;
const CONFETTI_THROTTLE_MS = 250;

let cssInjected = false;
function ensureCss() {
  if (cssInjected || typeof document === "undefined") return;
  cssInjected = true;
  const css = `
    .arabise-fly-star {
      position: fixed; left: 0; top: 0;
      width: 22px; height: 22px;
      pointer-events: none; z-index: 9999;
      will-change: transform, opacity;
      color: #e0c388;
      filter: drop-shadow(0 0 6px currentColor);
      transform: translate(-50%, -50%) scale(0.6);
      opacity: 0;
      animation: arabise-fly-star 1s cubic-bezier(0.22, 1, 0.36, 1) forwards;
    }
    .arabise-fly-star svg { width: 100%; height: 100%; fill: currentColor; }

    @keyframes arabise-fly-star {
      0%   { opacity: 0;   transform: translate(-50%,-50%) scale(0.4); }
      18%  { opacity: 1;   transform: translate(-50%,-50%) scale(1.05); }
      55%  { opacity: 1;   transform: translate(calc(-50% + var(--mid-x)), calc(-50% + var(--mid-y))) scale(0.9) rotate(120deg); }
      100% { opacity: 0;   transform: translate(calc(-50% + var(--dx)),    calc(-50% + var(--dy)))    scale(0.4) rotate(300deg); }
    }

    .arabise-points-bump {
      animation: arabise-points-bump 520ms cubic-bezier(0.22, 1, 0.36, 1);
    }
    @keyframes arabise-points-bump {
      0%   { transform: scale(1); }
      40%  { transform: scale(1.12); }
      100% { transform: scale(1); }
    }

    .arabise-confetti {
      position: fixed; left: 0; top: 0;
      pointer-events: none; z-index: 9999;
      will-change: transform, opacity;
      width: var(--size, 8px); height: var(--size, 8px);
      transform: translate(-50%, -50%);
      opacity: 0;
      animation: arabise-confetti 1.4s cubic-bezier(0.22, 1, 0.36, 1) forwards;
    }
    .arabise-confetti.shape-square { border-radius: 2px; }
    .arabise-confetti.shape-rect   { width: calc(var(--size) * 1.5); height: var(--size); border-radius: 2px; }
    .arabise-confetti.shape-circle { border-radius: 9999px; }

    @keyframes arabise-confetti {
      0%   { opacity: 0;   transform: translate(-50%,-50%) rotate(0deg) scale(0.5); }
      10%  { opacity: 1;                                                            }
      40%  { opacity: 1;   transform: translate(calc(-50% + var(--cx)), calc(-50% + var(--cy))) rotate(calc(var(--rot) * 0.5)) scale(1.1); }
      100% { opacity: 0;   transform: translate(calc(-50% + var(--ex)), calc(-50% + var(--ey))) rotate(var(--rot)) scale(0.8); }
    }
  `;
  const style = document.createElement("style");
  style.dataset.arabise = "effects";
  style.textContent = css;
  document.head.appendChild(style);
}

/* ───────────────── Stars ───────────────── */

/**
 * Fire golden stars from `fromEl` (typically the answer button) to the
 * `#arabise-points-pill` element in the top bar.
 */
export function flyStarsToPill(fromEl: Element | null, opts?: { count?: number }) {
  if (!fromEl) return;
  if (prefersReducedMotion()) return;
  ensureCss();

  const target = document.getElementById("arabise-points-pill");
  if (!target) return;

  const isMobile = window.innerWidth < 700;
  const count = opts?.count ?? (isMobile ? 4 : 7);

  const fromRect = fromEl.getBoundingClientRect();
  const toRect   = target.getBoundingClientRect();
  const fromX = fromRect.left + fromRect.width / 2;
  const fromY = fromRect.top  + fromRect.height / 2;
  const toX   = toRect.left   + toRect.width / 2;
  const toY   = toRect.top    + toRect.height / 2;

  for (let i = 0; i < count; i++) spawnStar(fromX, fromY, toX, toY, i, count);

  // Pulse the pill when stars land.
  window.setTimeout(() => target.classList.add("arabise-points-bump"), 580);
  window.setTimeout(() => target.classList.remove("arabise-points-bump"), 1100);
}

function spawnStar(fromX: number, fromY: number, toX: number, toY: number, i: number, total: number) {
  const el = document.createElement("span");
  el.className = "arabise-fly-star";
  el.innerHTML = STAR_SVG;
  el.style.left = fromX + "px";
  el.style.top  = fromY + "px";
  el.style.color = STAR_COLORS[Math.floor(Math.random() * STAR_COLORS.length)];

  const jitter = 18;
  const jx = (Math.random() - 0.5) * jitter;
  const jy = (Math.random() - 0.5) * jitter;
  const dx = (toX + jx) - fromX;
  const dy = (toY + jy) - fromY;
  const midX = dx * 0.5 + (Math.random() - 0.5) * 60;
  const midY = dy * 0.5 - 90 - Math.random() * 40;

  el.style.setProperty("--mid-x", midX + "px");
  el.style.setProperty("--mid-y", midY + "px");
  el.style.setProperty("--dx",    dx   + "px");
  el.style.setProperty("--dy",    dy   + "px");
  el.style.animationDelay = (i / total) * 90 + "ms";

  document.body.appendChild(el);
  window.setTimeout(() => el.parentNode?.removeChild(el), 1100);
}

/* ───────────────── Confetti ───────────────── */

export function confettiForResult(opts: { correct: number; total: number; originEl?: Element | null }) {
  const total   = opts.total   || 0;
  const correct = opts.correct || 0;
  if (total <= 0 || correct <= 0) return;
  const pct = (correct / total) * 100;
  const intensity = pct >= 80 ? 1.6 : pct >= 50 ? 1.0 : 0.6;
  confettiBurst({ originEl: opts.originEl, intensity });
}

export function confettiBurst(opts?: { x?: number; y?: number; originEl?: Element | null; intensity?: number }) {
  if (prefersReducedMotion()) return;
  ensureCss();
  const now = performance.now();
  if (now - lastConfettiAt < CONFETTI_THROTTLE_MS) return;
  lastConfettiAt = now;

  const o = opts || {};
  const intensity = Math.max(0.4, Math.min(o.intensity || 1, 1.6));
  const isMobile = window.innerWidth < 700;
  const baseCount = isMobile ? 18 : 32;
  const count = Math.round(baseCount * intensity);

  let x: number, y: number;
  if (typeof o.x === "number" && typeof o.y === "number") {
    x = o.x; y = o.y;
  } else if (o.originEl) {
    const r = o.originEl.getBoundingClientRect();
    x = r.left + r.width / 2;
    y = r.top  + r.height / 2;
  } else {
    x = window.innerWidth / 2;
    y = window.innerHeight / 3;
  }

  for (let i = 0; i < count; i++) spawnConfetti(x, y, i, count, intensity);
}

function spawnConfetti(x: number, y: number, i: number, total: number, intensity: number) {
  const el = document.createElement("span");
  el.className = "arabise-confetti";
  const color = CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)];
  const shape = CONFETTI_SHAPES[Math.floor(Math.random() * CONFETTI_SHAPES.length)];
  el.classList.add("shape-" + shape);
  el.style.background = color;
  el.style.left = x + "px";
  el.style.top  = y + "px";

  const angle = -Math.PI * (0.166 + Math.random() * 0.666);
  const dist  = (120 + Math.random() * 180) * intensity;
  const peakX = Math.cos(angle) * dist;
  const peakY = Math.sin(angle) * dist;
  const endX  = peakX + (Math.random() - 0.5) * 80;
  const endY  = peakY + 280 + Math.random() * 140;
  const rot   = (Math.random() - 0.5) * 720;
  const size  = 6 + Math.floor(Math.random() * 6);

  el.style.setProperty("--cx",   peakX + "px");
  el.style.setProperty("--cy",   peakY + "px");
  el.style.setProperty("--ex",   endX  + "px");
  el.style.setProperty("--ey",   endY  + "px");
  el.style.setProperty("--rot",  rot   + "deg");
  el.style.setProperty("--size", size  + "px");
  el.style.animationDelay = (i / total) * 60 + "ms";

  document.body.appendChild(el);
  window.setTimeout(() => el.parentNode?.removeChild(el), 1700);
}
